// ============================================================================
// FSA TRIAL BALANCE TAB  (FSA-2.1.7)
// ============================================================================
// Central editable grid of the engagement's CONSOLIDATED TB. Replaces the
// pop-up Unmapped Queue as the day-to-day TB review surface.
//
// Columns (8 mandatory, 4 optional via ColumnSelector — persisted to
// localStorage):
//   Ledger | Group | Amount | Schedule | Classification | Control | Journals | Actions
//   (optional: Class. Source, Classified On, Classified By, Opening Amount)
//
// Filter chips above the grid drive the server-side classification filter
// (all / classified / schedule_only / unclassified / control). Search input
// debounces 400ms into the entries query.
//
// Action menu items are intentionally deferred — pending the mapping rework
// the user flagged. A placeholder "Actions — coming soon" item keeps the
// menu trigger discoverable for now.

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  ColumnSelector,
  type ColumnDefinition,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import { useTrialBalanceEntries } from 'hooks/api/fsa/useFsaTrialBalance';
import { useCoverage } from 'hooks/api/fsa/useFsaClassification';
import { useFsaTemplateHierarchy } from 'hooks/api/fsa/useFsaTemplates';
import { useFsaEngagementCustomClasses } from 'hooks/api/fsa/useFsaNotes';
import { useAuditedLedgerBalances } from 'hooks/api/fsa/useFsaAdjustmentJournals';
import { fsaClassificationService } from 'api/services/fsa/fsaClassificationService';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import { exportToExcel } from 'utils';
import notificationService from 'services/notificationService';
import FsaEditClassificationModal from 'components/modals/modules/fsa/FsaEditClassificationModal';
import AutoClassifyPreviewModal from 'components/modals/modules/fsa/AutoClassifyPreviewModal';
import FsaTrialBalanceEntryViewModal from 'components/modals/modules/fsa/FsaTrialBalanceEntryViewModal';
import { ConfirmDialog } from 'components/modals';
import type { ID } from 'api/types';
import type {
  TrialBalanceEntry,
  ClassificationFilter,
  TrialBalanceEntryQuery,
} from 'api/types/fsa/trialBalance.types';

interface Props {
  /** The engagement's CONSOLIDATED TB id; null when no TB has been uploaded yet. */
  trialBalanceId: ID | null;
  /** The template the engagement is using — drives the Schedule filter dropdown. */
  templateId?: ID | null;
  /**
   * FSA-2.2 step 4b — engagement id. Surfaced to the Edit Classification
   * modal so its picker can expose engagement-time custom classes
   * alongside template classes. Null when no engagement is in scope (the
   * tab won't usually render in that state, but the prop is optional).
   */
  engagementId?: ID | null;
  /**
   * FSA-2.2 Adjustments — bumped by the parent screen whenever any JE
   * is posted / reversed / saved on the engagement (the Adjustments tab
   * raises `onJournalChanged`, the parent forwards the increment here).
   * The TB tab listens via useEffect and refetches `auditedBalances` so
   * the Journal Net + Audited columns reflect the latest posting state
   * without the auditor having to switch back and forth. Reverse and
   * delete-of-DRAFT also bump the signal so all paths converge.
   */
  journalRefreshSignal?: number;
  /** PRIMA tab identity — required for the Edit Classification modal's minimize-to-bar support. */
  tabId?: string;
  moduleName?: string;
}

// FSA-2.4 UI polish — chip strip slimmed to the three high-frequency
// filters. Dropped:
//   • 'Classified'      — auditors rarely filter TO already-done rows;
//                          coverage % indicator (right of chips) gives
//                          the same information without the noise.
//   • 'Control accounts'— niche; surfaced via the Ctrl column filter in
//                          the side panel instead. The chip back-end
//                          handler still supports these IDs in case a
//                          deep-link / saved-view references them.
const CLASSIFICATION_CHIPS: { id: ClassificationFilter; label: string }[] = [
  { id: 'all',           label: 'All' },
  { id: 'unclassified',  label: 'Unmapped' },
  { id: 'schedule_only', label: 'Schedule-only' },
];

const COLUMN_DEFS: ColumnDefinition[] = [
  // Mandatory (visible by default)
  { id: 'ledgerName',       label: 'Ledger',         field: 'ledgerName',        required: true,  defaultVisible: true,  sortable: true  },
  { id: 'parentGroup',      label: 'Group',          field: 'parentGroup',       required: false, defaultVisible: true,  sortable: true  },
  // FSA-2.4 UI polish — column order changed to match audit-report
  // convention (CY-first, then PY). Audited first; Opening adjacent so the
  // CY ↔ PY comparison reads left-to-right.
  // Consolidated + Journal Net dropped from the grid (and column
  // selector) — both values surface in the View row action's modal
  // breakdown. Excel export still carries them.
  { id: 'audited',          label: 'Audited',        field: '',                  required: false, defaultVisible: true,  sortable: false },
  { id: 'openingAmount',    label: 'Opening',        field: 'openingAmount',     required: false, defaultVisible: true,  sortable: true  },
  { id: 'scheduleName',     label: 'Schedule',       field: 'scheduleName',      required: false, defaultVisible: true,  sortable: false },
  { id: 'classification',   label: 'Classification', field: 'scheduleClassName', required: false, defaultVisible: true,  sortable: false },
  { id: 'classifySource',   label: 'Class. Source',  field: 'isAutoClassified',  required: false, defaultVisible: true,  sortable: false },
  // 'journals' column dropped (FSA-2.4) — was a stub showing "—" since
  // FSA-2.2 superseded it with Journal Net. Removing both the column
  // definition AND the column renderer; the persisted layout key is
  // bumped so saved preferences from v3 don't keep referencing it.
  // Optional (hidden by default)
  { id: 'isControlAccount', label: 'Control Account (Ctrl)', field: 'isControlAccount', required: false, defaultVisible: false, sortable: false },
  { id: 'classifiedAt',     label: 'Classified On',  field: 'classifiedAt',      required: false, defaultVisible: false, sortable: true  },
  { id: 'classifiedBy',     label: 'Classified By',  field: 'classifiedByName',  required: false, defaultVisible: false, sortable: false },
];

// v4: Opening promoted to visible-by-default; legacy 'journals' stub removed.
// v5 — Consolidated + Journal Net removed from grid + column selector
// (View modal now hosts the full breakdown). Key bump invalidates the
// v4 saved layouts that referenced those ids.
const COLUMN_PREF_KEY = 'fsa-trial-balance-grid-columns-v5';

/** Round a signed amount to 2 decimals — used by the XLSX export so Excel reads clean numbers. */
const round2 = (n: number | null | undefined): number =>
  Math.round(((n ?? 0) + Number.EPSILON) * 100) / 100;

const FsaTrialBalanceTab: React.FC<Props> = ({ trialBalanceId, templateId, engagementId, journalRefreshSignal, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  // Auto-minimize bridge — TrialBalanceTab calls this when the user opens a
  // different row's Edit modal while another one is open.
  const editModalMinimizeRef = useRef<(() => void) | null>(null);
  // Schedule options for the filter dropdown — pulled from the engagement's
  // template hierarchy. Flat list of every schedule across every report (BS
  // line groups + each Note). Format: "<report code> · <schedule name>".
  const { data: hierarchy } = useFsaTemplateHierarchy(templateId ?? null);
  const scheduleOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [{ value: '', label: 'All schedules' }];
    if (!hierarchy?.reports) return opts;
    hierarchy.reports.forEach(r => {
      r.schedules?.forEach(node => {
        const s = node.schedule;
        if (!s?.id) return;
        opts.push({
          value: String(s.id),
          label: `${r.report.code} · ${s.name}`,
        });
      });
    });
    return opts;
  }, [hierarchy]);
  // ---------- Filters ----------
  const [classification, setClassification] = useState<ClassificationFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filter side panel state — held separately from "applied" values so the
  // panel can be edited freely until Apply is clicked.
  const [filterOpen, setFilterOpen] = useState(false);
  const [groupInput, setGroupInput] = useState('');
  const [minAmountInput, setMinAmountInput] = useState('');
  const [maxAmountInput, setMaxAmountInput] = useState('');
  const [amountSignInput, setAmountSignInput] = useState<'all' | 'dr' | 'cr'>('all');
  const [scheduleInput, setScheduleInput] = useState<string>('');
  const [classifySourceInput, setClassifySourceInput] = useState<'all' | 'auto' | 'manual'>('all');

  const [appliedGroup, setAppliedGroup] = useState('');
  const [appliedMinAmount, setAppliedMinAmount] = useState<number | null>(null);
  const [appliedMaxAmount, setAppliedMaxAmount] = useState<number | null>(null);
  const [appliedAmountSign, setAppliedAmountSign] = useState<'all' | 'dr' | 'cr'>('all');
  const [appliedScheduleId, setAppliedScheduleId] = useState<string>('');
  const [appliedClassifySource, setAppliedClassifySource] = useState<'all' | 'auto' | 'manual'>('all');

  const activeFilterCount =
    (appliedGroup ? 1 : 0)
    + (appliedMinAmount !== null ? 1 : 0)
    + (appliedMaxAmount !== null ? 1 : 0)
    + (appliedAmountSign !== 'all' ? 1 : 0)
    + (appliedScheduleId ? 1 : 0)
    + (appliedClassifySource !== 'all' ? 1 : 0);

  const handleApplyFilters = useCallback(() => {
    setAppliedGroup(groupInput.trim());
    const min = minAmountInput.trim() === '' ? null : Number(minAmountInput);
    const max = maxAmountInput.trim() === '' ? null : Number(maxAmountInput);
    setAppliedMinAmount(Number.isFinite(min as number) ? (min as number) : null);
    setAppliedMaxAmount(Number.isFinite(max as number) ? (max as number) : null);
    setAppliedAmountSign(amountSignInput);
    setAppliedScheduleId(scheduleInput);
    setAppliedClassifySource(classifySourceInput);
    setFilterOpen(false);
  }, [groupInput, minAmountInput, maxAmountInput, amountSignInput, scheduleInput, classifySourceInput]);

  const handleClearFilters = useCallback(() => {
    setGroupInput('');
    setMinAmountInput('');
    setMaxAmountInput('');
    setAmountSignInput('all');
    setScheduleInput('');
    setClassifySourceInput('all');
    setAppliedGroup('');
    setAppliedMinAmount(null);
    setAppliedMaxAmount(null);
    setAppliedAmountSign('all');
    setAppliedScheduleId('');
    setAppliedClassifySource('all');
  }, []);

  // ---------- Pagination ----------
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Reset to page 1 whenever filter / search changes.
  useEffect(() => { setPage(1); }, [
    classification, appliedSearch,
    appliedGroup, appliedMinAmount, appliedMaxAmount, appliedAmountSign,
    appliedScheduleId, appliedClassifySource,
  ]);

  const query = useMemo<TrialBalanceEntryQuery>(() => ({
    page,
    pageSize,
    classification,
    searchTerm: appliedSearch.trim() || undefined,
    groupTerm: appliedGroup || undefined,
    minAmount: appliedMinAmount ?? undefined,
    maxAmount: appliedMaxAmount ?? undefined,
    amountSign: appliedAmountSign === 'all' ? undefined : appliedAmountSign,
    scheduleId: appliedScheduleId || undefined,
    classificationSource: appliedClassifySource === 'all' ? undefined : appliedClassifySource,
  }), [
    page, pageSize, classification, appliedSearch,
    appliedGroup, appliedMinAmount, appliedMaxAmount, appliedAmountSign,
    appliedScheduleId, appliedClassifySource,
  ]);

  const { data: rows, pagination, loading, refetch } = useTrialBalanceEntries(trialBalanceId, query);
  const { data: coverage, refetch: refetchCoverage } = useCoverage(trialBalanceId);

  // FSA-2.2 Adjustments — per-ledger 3-column composition (Consolidated /
  // Journal Net / Audited). Keyed by lower-trimmed ledger name to match the
  // backend aggregation key.
  const { data: auditedBalances, refetch: refetchAuditedBalances } =
    useAuditedLedgerBalances(engagementId ?? null);
  // Refetch audited balances whenever a JE is posted / reversed / saved
  // in the sibling Adjustments tab. Parent screen bumps
  // `journalRefreshSignal`; this effect picks the change up and pulls
  // fresh Journal Net + Audited columns. Skips the initial render
  // (the hook itself loads on mount) by gating on signal > 0.
  useEffect(() => {
    if (journalRefreshSignal != null && journalRefreshSignal > 0) {
      refetchAuditedBalances();
      // Badge counts depend on classification state which a JE post can't
      // change today, but refresh anyway so the totals strip (which reads
      // ConsolidatedTotal from coverage) and any future JE-side classify
      // hook stays in sync without an extra signal.
      refetchCoverage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalRefreshSignal]);

  // FSA-2.4 — Refresh badge counts + totals whenever the TB identity OR
  // the pagination's totalItems changes. A fresh TB upload keeps the
  // consolidated TB id stable (the row is persistent — only its entries
  // cycle), so `useCoverage` doesn't auto-refire on its trialBalanceId
  // dependency. But the consolidator regenerates entries on every upload,
  // so the entries query's totalItems will change → use that as our
  // upload-detection signal and refresh coverage in tandem.
  useEffect(() => {
    if (!trialBalanceId) return;
    refetchCoverage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialBalanceId, pagination.totalItems]);
  const auditedByLedger = useMemo(() => {
    const m = new Map<string, {
      consolidated: number; journalNet: number; audited: number; isJeCreated: boolean;
      openingJournalNet: number;
    }>();
    for (const r of auditedBalances ?? []) {
      m.set(r.ledgerName.trim().toLowerCase(), {
        consolidated: r.consolidated,
        journalNet: r.journalNet,
        audited: r.audited,
        isJeCreated: r.isJeCreated,
        openingJournalNet: r.openingJournalNet ?? 0,
      });
    }
    return m;
  }, [auditedBalances]);
  // ADR-0026 — Σ OPENING journals across the engagement (totals strip).
  const openingJournalTotal = useMemo(
    () => (auditedBalances ?? []).reduce((s, b) => s + (b.openingJournalNet ?? 0), 0),
    [auditedBalances],
  );
  const lookupAudited = useCallback((ledgerName: string) => {
    return auditedByLedger.get((ledgerName ?? '').trim().toLowerCase());
  }, [auditedByLedger]);

  // FSA-2.2 step 4b — engagement-time custom classes (UUID local_id → label).
  // Drives the Classification column chip when an entry is mapped to a
  // custom class. Cheap engagement-scoped lookup; refetched on classify.
  const { data: engagementCustomClasses, refetch: refetchCustomClasses } =
    useFsaEngagementCustomClasses(engagementId ?? null);
  const customClassNameByLocalId = useMemo(() => {
    const m = new Map<string, string>();
    (engagementCustomClasses ?? []).forEach(c => m.set(c.localId, c.name));
    return m;
  }, [engagementCustomClasses]);

  // ---------- Column visibility ----------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    () => COLUMN_DEFS.filter(c => c.defaultVisible).map(c => c.id)
  );
  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) setVisibleColumns(parsed);
    } catch { /* ignore corrupt pref */ }
  }, []);
  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    try { localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols)); } catch { /* ignore */ }
  }, []);
  const handleResetColumns = useCallback(() => {
    const defaults = COLUMN_DEFS.filter(c => c.defaultVisible).map(c => c.id);
    setVisibleColumns(defaults);
    try { localStorage.removeItem(COLUMN_PREF_KEY); } catch { /* ignore */ }
  }, []);

  // ---------- Action menu (FSA-2.1.8) ----------
  const [editEntry, setEditEntry] = useState<TrialBalanceEntry | null>(null);
  const [clearEntry, setClearEntry] = useState<TrialBalanceEntry | null>(null);
  const [controlEntry, setControlEntry] = useState<TrialBalanceEntry | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [isFlippingControl, setIsFlippingControl] = useState(false);
  const [signSweepBusyId, setSignSweepBusyId] = useState<string | number | null>(null);
  // FSA-2.4 UI polish — View Details row action: read-only popup showing
  // the full per-ledger breakdown (Opening + Consolidated + Journal Net
  // + Audited + classification metadata). Replaces the Consolidated /
  // Journal Net columns dropped from the grid.
  const [viewEntry, setViewEntry] = useState<TrialBalanceEntry | null>(null);
  const [autoClassifyOpen, setAutoClassifyOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  /**
   * FSA-2.3 — exports the consolidated TB merged with posted Adjustment
   * Journal effects (Audited Closing TB) to XLSX. Backend builds the row
   * set in one call (`/export-audited`); this handler dumps it to a
   * worksheet via the shared `exportToExcel` util, with a totals row
   * appended for the signed Debit / Credit split per amount column.
   */
  const handleExportAuditedTb = useCallback(async () => {
    if (!trialBalanceId) return;
    setIsExporting(true);
    try {
      const response = await fsaTrialBalanceService.exportAudited(trialBalanceId);
      const rows = response.data ?? [];
      if (rows.length === 0) {
        notificationService.toast.info('No ledgers to export.');
        return;
      }

      // Totals row — sum each signed amount column. Excel reads these as
      // simple numbers; the auditor can re-sum / pivot from there.
      const totals = rows.reduce((acc, r) => ({
        openingTb: acc.openingTb + (r.openingConsolidated ?? 0),
        openingAdj: acc.openingAdj + (r.openingJournalNet ?? 0),
        opening: acc.opening + (r.openingAmount ?? 0),
        consolidated: acc.consolidated + (r.closingConsolidated ?? 0),
        journalNet: acc.journalNet + (r.journalNet ?? 0),
        audited: acc.audited + (r.closingAudited ?? 0),
      }), { openingTb: 0, openingAdj: 0, opening: 0, consolidated: 0, journalNet: 0, audited: 0 });

      // Class Name comes pre-resolved from the backend: leaf classes via
      // JOIN on tbl_schedule_class, engagement-time custom classes via
      // server-side workspace JSON parse. The FE just consumes
      // r.scheduleClassName here — no client-side map lookup that could
      // race against the engagement-custom-classes fetch.
      const flatRows = rows.map(r => ({
        Ledger:                r.ledgerName,
        'Parent Group':        r.parentGroup ?? '',
        'Note Code':           r.scheduleCode ?? '',
        'Note Name':           r.scheduleName ?? '',
        'Class Code':          r.scheduleClassCode ?? (r.customClassLocalId ? 'CUSTOM' : ''),
        'Class Name':          r.scheduleClassName ?? '',
        'Class Source':        r.scheduleClassCode ? 'Template' : (r.customClassLocalId ? 'Custom' : ''),
        'Control Account':     r.isControlAccount ? 'Yes' : '',
        'JE-only Ledger':      r.isJeCreated ? 'Yes' : '',
        // ADR-0026 — opening split: TB → opening adjustments → audited.
        'Opening (TB)':        round2(r.openingConsolidated),
        'Opening Adjustments': round2(r.openingJournalNet),
        'Opening (Audited)':   round2(r.openingAmount),
        'Closing (Consolidated)': round2(r.closingConsolidated),
        'Journal Net':         round2(r.journalNet),
        'Closing (Audited)':   round2(r.closingAudited),
      }));
      flatRows.push({
        Ledger:                'TOTAL',
        'Parent Group':        '',
        'Note Code':           '',
        'Note Name':           '',
        'Class Code':          '',
        'Class Name':          '',
        'Class Source':        '',
        'Control Account':     '',
        'JE-only Ledger':      '',
        'Opening (TB)':        round2(totals.openingTb),
        'Opening Adjustments': round2(totals.openingAdj),
        'Opening (Audited)':   round2(totals.opening),
        'Closing (Consolidated)': round2(totals.consolidated),
        'Journal Net':         round2(totals.journalNet),
        'Closing (Audited)':   round2(totals.audited),
      });

      const stamp = new Date().toISOString().slice(0, 10);
      exportToExcel({
        data: flatRows,
        fileName: `audited-tb-${stamp}`,
      });
      notificationService.toast.success(`Exported ${rows.length} ledgers to Excel.`);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Failed to export audited TB.'
      );
    } finally {
      setIsExporting(false);
    }
  }, [trialBalanceId]);

  const handleClearClassification = useCallback(async () => {
    if (!clearEntry || !trialBalanceId) return;
    setIsClearing(true);
    try {
      await fsaClassificationService.bulkClassify(trialBalanceId, {
        entryIds: [clearEntry.id],
        scheduleClassId: null,
      });
      notificationService.toast.success(`Classification cleared on '${clearEntry.ledgerName}'.`);
      setClearEntry(null);
      refetch();
      refetchCoverage();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to clear classification.');
    } finally {
      setIsClearing(false);
    }
  }, [clearEntry, trialBalanceId, refetch, refetchCoverage]);

  const handleToggleSignSweepExclude = useCallback(async (row: TrialBalanceEntry) => {
    if (!trialBalanceId) return;
    setSignSweepBusyId(row.id);
    try {
      await fsaTrialBalanceService.bulkExcludeFromSignSweep(trialBalanceId, {
        entryIds: [row.id],
        excludeFromSignSweep: !row.excludeFromSignSweep,
      });
      notificationService.toast.success(
        row.excludeFromSignSweep
          ? `'${row.ledgerName}' re-included in sign sweep.`
          : `'${row.ledgerName}' excluded from sign sweep.`
      );
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to flip sign-sweep flag.');
    } finally {
      setSignSweepBusyId(null);
    }
  }, [trialBalanceId, refetch]);

  const handleToggleControl = useCallback(async () => {
    if (!controlEntry || !trialBalanceId) return;
    setIsFlippingControl(true);
    try {
      await fsaTrialBalanceService.bulkControlAccount(trialBalanceId, {
        entryIds: [controlEntry.id],
        isControlAccount: !controlEntry.isControlAccount,
      });
      notificationService.toast.success(
        controlEntry.isControlAccount
          ? `'${controlEntry.ledgerName}' un-marked as control account.`
          : `'${controlEntry.ledgerName}' marked as control account.`
      );
      setControlEntry(null);
      refetch();
      refetchCoverage();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to flip control-account flag.');
    } finally {
      setIsFlippingControl(false);
    }
  }, [controlEntry, trialBalanceId, refetch, refetchCoverage]);

  // Edit handler — minimize-aware. Mirrors the pattern in PRIMA's existing
  // engagement / employee screens (CLAUDE.md → Minimize-aware handlers).
  const handleEditClick = useCallback((row: TrialBalanceEntry) => {
    if (tabId) {
      const modalId = `${tabId}-fsa-edit-cls-${row.id}`;
      // (a) same row is already minimized → restore it.
      if (isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
    }
    // (b) a different row's modal is currently open → auto-minimize it first
    //     so the user doesn't lose unsaved work, then open the new one.
    if (editEntry && editEntry.id !== row.id && editModalMinimizeRef.current) {
      editModalMinimizeRef.current();
    }
    setEditEntry(row);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, editEntry]);

  // Restore callback wired into the modal — re-opens it for the previously-
  // minimized entry. The modal hands back the FULL entry object it captured
  // in its closure at minimize time, so we don't need to find it in the
  // current `rows` page (which may have changed since the modal was
  // minimized).
  const handleRestoreEditModal = useCallback((entry: TrialBalanceEntry) => {
    setEditEntry(entry);
  }, []);

  const getRowActions = useCallback((row: TrialBalanceEntry): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'view',
        label: 'View Details',
        icon: <IconSystem.eye size={16} />,
        onClick: () => setViewEntry(row),
      },
      {
        id: 'edit',
        label: 'Edit Classification…',
        icon: <IconSystem.edit size={16} />,
        onClick: () => handleEditClick(row),
      },
    ];
    if (row.scheduleId || row.scheduleClassId) {
      items.push({
        id: 'clear',
        label: 'Clear Classification',
        icon: <IconSystem.trash size={16} />,
        onClick: () => setClearEntry(row),
      });
    }
    items.push({
      id: 'control',
      label: row.isControlAccount ? 'Un-mark Control Account' : 'Mark as Control Account',
      icon: <IconSystem.settings size={16} />,
      onClick: () => setControlEntry(row),
    });
    // FSA-2.2 sign-sweep opt-out — surface only when the entry is mapped
    // to a schedule (the flag is a no-op on unmapped / control-account
    // entries). On schedules without an auto_sweep config the flag still
    // has no effect, so the action is harmless there too.
    if (row.scheduleId) {
      items.push({
        id: 'sign-sweep',
        label: row.excludeFromSignSweep ? 'Re-include in Sign Sweep' : 'Exclude from Sign Sweep',
        icon: <IconSystem.refresh size={16} />,
        onClick: () => handleToggleSignSweepExclude(row),
        disabled: signSweepBusyId === row.id,
      });
    }
    return items;
  }, [handleToggleSignSweepExclude, signSweepBusyId]);

  // ---------- Columns (full set; SyutGrid sees only visible ones) ----------
  const allTableColumns = useMemo<SyutGridColumn<TrialBalanceEntry>[]>(() => [
    {
      id: 'ledgerName',
      header: 'Ledger',
      accessorKey: 'ledgerName',
      size: 220,
      enableSorting: true,
      // FSA-2.4 UI polish — long ledger names ellipsis + hover tooltip.
      // Frees horizontal space; full name remains accessible on hover.
      cell: ({ row }) => (
        <strong
          title={row.original.ledgerName}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.original.ledgerName}
        </strong>
      ),
    },
    {
      id: 'parentGroup',
      header: 'Group',
      accessorKey: 'parentGroup',
      size: 140,
      enableSorting: true,
      cell: ({ row }) => row.original.parentGroup
        ? <span
            title={row.original.parentGroup}
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >{row.original.parentGroup}</span>
        : <span style={dashStyle}>—</span>,
    },
    // FSA-2.4 UI polish — Audited → Opening order matches audit-report
    // convention (CY first, then PY). Consolidated + Journal Net columns
    // dropped from the grid; both values are accessible via the View row
    // action's modal breakdown. Excel export still carries them.
    {
      id: 'audited',
      header: 'Audited',
      size: 125,
      enableSorting: false,
      cell: ({ row }) => {
        const a = lookupAudited(row.original.ledgerName);
        // Fall back to raw consolidated when no JE composition row exists
        // (engagement has no posted JEs yet) — audited = consolidated.
        const value = a ? a.audited : row.original.closingAmount;
        return (
          <span style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <AmountCell value={value} />
            {a?.isJeCreated && (
              <span
                title="JE-introduced ledger — not in the source TB, created by a posted journal entry."
                style={jeIntroducedBadgeStyle}
              >
                JE
              </span>
            )}
          </span>
        );
      },
    },
    {
      // ADR-0026 — the ADJUSTED opening (TB opening + opening adjustment
      // journals); an "ADJ" marker shows when an opening journal touches
      // the ledger. Sorting stays on the TB opening (server-side).
      id: 'openingAmount',
      header: 'Opening',
      accessorKey: 'openingAmount',
      size: 125,
      enableSorting: true,
      cell: ({ row }) => {
        const adj = lookupAudited(row.original.ledgerName)?.openingJournalNet ?? 0;
        const hasAdj = Math.abs(adj) >= 0.005;
        return (
          <span style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <AmountCell value={row.original.openingAmount + adj} />
            {hasAdj && (
              <span
                title={`Opening adjusted by an opening balance adjustment journal — TB opening ${fmtSigned(row.original.openingAmount)}, adjustments ${fmtSigned(adj)}.`}
                style={openingAdjBadgeStyle}
              >
                ADJ
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'scheduleName',
      header: 'Schedule',
      accessorKey: 'scheduleName',
      size: 200,
      enableSorting: false,
      cell: ({ row }) => row.original.scheduleName
        ? <span>{row.original.scheduleName}</span>
        : <span style={dashStyle}>—</span>,
    },
    {
      id: 'classification',
      header: 'Classification',
      accessorKey: 'scheduleClassName',
      size: 190,
      enableSorting: false,
      cell: ({ row }) => (
        <ClassificationChip
          entry={row.original}
          customClassName={
            row.original.customClassLocalId
              ? customClassNameByLocalId.get(row.original.customClassLocalId)
              : undefined
          }
        />
      ),
    },
    {
      id: 'isControlAccount',
      header: 'Ctrl',
      accessorKey: 'isControlAccount',
      size: 60,
      enableSorting: false,
      cell: ({ row }) => row.original.isControlAccount
        ? <span style={chipStyle('#f3e8ff', '#6b21a8')} title="Control account">Y</span>
        : <span style={dashStyle}>—</span>,
    },
    // FSA-2.4 — legacy 'journals' stub column removed; Journal Net column
    // (above) carries the real per-ledger adjustment-journal totals.
    // Optional columns ───────────────────────────────────────────────
    {
      id: 'classifySource',
      header: 'Source',
      size: 90,
      enableSorting: false,
      cell: ({ row }) => {
        const e = row.original;
        if (!e.scheduleClassId) return <span style={dashStyle}>—</span>;
        return e.isAutoClassified
          ? <span style={chipStyle('#dbeafe', '#1e40af')}>Auto</span>
          : <span style={chipStyle('#dcfce7', '#166534')}>Manual</span>;
      },
    },
    {
      id: 'classifiedAt',
      header: 'Classified On',
      accessorKey: 'classifiedAt',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => <span style={{ fontSize: '0.8rem' }}>{formatDateTime(row.original.classifiedAt)}</span>,
    },
    {
      id: 'classifiedBy',
      header: 'Classified By',
      accessorKey: 'classifiedByName',
      size: 140,
      enableSorting: false,
      cell: ({ row }) => row.original.classifiedByName
        ? <span
            title={row.original.classifiedByName}
            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >{row.original.classifiedByName}</span>
        : <span style={dashStyle}>—</span>,
    },
    // `lookupAudited` is a useCallback whose identity flips whenever the
    // refetched audited-balances data lands. Without it in the deps, the
    // memo keeps a STALE `lookupAudited` closure baked into each cell
    // function — Journal Net / Audited columns then render against the
    // first-mount auditedByLedger Map forever, even though the hook
    // already has fresh data. The only way to "fix" that without this
    // dep was to close + reopen the workspace tab (full re-mount), which
    // is exactly the symptom the auditor reported.
  ], [customClassNameByLocalId, lookupAudited]);

  const visibleTableColumns = useMemo<SyutGridColumn<TrialBalanceEntry>[]>(
    () => allTableColumns.filter(c => visibleColumns.includes(c.id as string)),
    [allTableColumns, visibleColumns],
  );

  // Chip counts derived from coverage. `control` isn't on the coverage DTO —
  // compute as residual (total − classified − scheduleOnly − unclassified).
  const chipCount = useCallback((id: ClassificationFilter): number | null => {
    if (!coverage) return null;
    switch (id) {
      case 'all':           return coverage.total;
      case 'classified':    return coverage.classified;
      case 'schedule_only': return coverage.scheduleOnly;
      case 'unclassified':  return coverage.unclassified;
      case 'control':       return Math.max(0, coverage.total - coverage.classified - coverage.scheduleOnly - coverage.unclassified);
      default: return null;
    }
  }, [coverage]);

  // ---------- Empty state ----------
  if (!trialBalanceId) {
    return (
      <div style={emptyStateStyle}>
        <IconSystem.alert size={28} />
        <div style={{ fontWeight: 600, marginTop: 8 }}>No trial balance uploaded yet</div>
        <div style={{ fontSize: '0.85rem', marginTop: 4, color: 'var(--text-secondary)' }}>
          Upload a TB from the <strong>General / Basic</strong> tab first.
        </div>
      </div>
    );
  }

  // ---------- Render ----------
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Compact toolbar: filter chips · coverage % · search · column selector · refresh */}
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
          {CLASSIFICATION_CHIPS.map(c => {
            const active = classification === c.id;
            const count = chipCount(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setClassification(c.id)}
                style={{ ...filterChipStyle, ...(active ? filterChipActiveStyle : {}) }}
                title={`Show ${c.label.toLowerCase()}`}
              >
                {c.label}
                {count != null && (
                  <span style={chipCountBadgeStyle(active)}>
                    {count.toLocaleString('en-IN')}
                  </span>
                )}
              </button>
            );
          })}
          {/* Coverage % pill — replaces the dropped "Classified" chip. */}
          {coverage && coverage.total > 0 && (() => {
            const pct = Math.round((coverage.classified / coverage.total) * 100);
            const remaining = coverage.total - coverage.classified;
            return (
              <span
                style={coveragePillStyle(pct)}
                title={`${coverage.classified.toLocaleString('en-IN')} of ${coverage.total.toLocaleString('en-IN')} ledgers classified (${remaining.toLocaleString('en-IN')} remaining)`}
              >
                <IconSystem.check size={11} /> {pct}% classified
              </span>
            );
          })()}
        </div>
        <div style={{ flex: 1 }} />
        <div style={searchWrapStyle}>
          <IconSystem.search size={14} style={searchIconStyle} />
          <input
            type="text"
            className="form-input"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search ledger / group…"
            style={searchInputStyle}
          />
        </div>
        {/* Per-screen minimized-modal strip — sits between Search and
            Auto-classify so any parked Edit Classification modal is
            restorable inline without leaving the TB tab. Auto-hides when
            nothing is parked. */}
        {tabId && <MinimizedModalStrip tabId={tabId} />}
        {/* Auto-classify button TEMPORARILY HIDDEN — feature disabled
            pending hint-rule review (auto-classification was producing
            incorrect mappings in UAT). Backend upload-time auto-classify
            is also gated off in TrialBalanceService.cs. Re-enable both
            sites together once the rules are validated. */}
        {false && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setAutoClassifyOpen(true)}
            title="Auto-classify unmapped ledgers from the hint dictionary + prior engagement"
          >
            <IconSystem.check size={14} /> Auto-classify
          </button>
        )}
        <ColumnSelector
          columns={COLUMN_DEFS}
          visibleColumns={visibleColumns}
          onApply={handleApplyColumns}
          onReset={handleResetColumns}
        />
        <button
          type="button"
          className={`btn btn-sm ${filterOpen || activeFilterCount > 0 ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilterOpen(o => !o)}
          title="Filter"
          style={{ position: 'relative' }}
        >
          <IconSystem.filter size={14} />
          {activeFilterCount > 0 && (
            <span style={filterBadgeStyle}>{activeFilterCount}</span>
          )}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleExportAuditedTb}
          title={isExporting ? 'Exporting…' : 'Export Audited TB to Excel'}
          disabled={!trialBalanceId || isExporting}
          aria-label="Export Audited TB to Excel"
        >
          {isExporting
            ? <IconSystem.refresh size={14} style={{ animation: 'spin 0.8s linear infinite' }} />
            : <IconSystem.download size={14} />}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => { refetch(); refetchAuditedBalances(); refetchCoverage(); }}
          title="Refresh entries + audited balances"
          disabled={loading}
        >
          <IconSystem.refresh size={14} />
        </button>
      </div>

      {/* FSA-2.4 — TOTAL strip. Aggregates across ALL entries on this TB
          (not just the current page). Opening + Consolidated come from
          coverage (server-side SUM). Journal Net + Audited derive from
          auditedBalances which carries one row per ledger across the
          engagement. A balanced TB shows all four columns netting to ~0;
          a visibly non-zero value flags an out-of-balance state for the
          auditor to chase down before finalising. */}
      {coverage && (
        <div style={totalsStripStyle}>
          <span style={totalsLabelStyle}>TOTAL ({coverage.total} ledgers)</span>
          <TotalsCell label="Opening"      value={coverage.openingTotal + openingJournalTotal} />
          <TotalsCell label="Consolidated" value={coverage.consolidatedTotal} />
          <TotalsCell label="Journal Net"  value={(auditedBalances ?? []).reduce((s, b) => s + (b.journalNet ?? 0), 0)} />
          <TotalsCell
            label="Audited"
            value={coverage.consolidatedTotal + (auditedBalances ?? []).reduce((s, b) => s + (b.journalNet ?? 0), 0)}
            emphasized
          />
        </div>
      )}

      {/* Grid + filter side panel */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <SyutGrid
            data={rows as any}
            columns={visibleTableColumns as any}
            enablePagination
            manualPagination
            totalCount={pagination.totalItems}
            currentPage={Math.max(0, page - 1)}
            pageSize={pageSize}
            onPageChange={(idx: number) => setPage(idx + 1)}
            onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
            enableSorting
            rowActions={getRowActions as any}
            frozenRightColumns={['actions']}
            emptyMessage={loading ? 'Loading…' : 'No entries match the current filter.'}
          />
        </div>

        {/* Slide-in filter panel — peer of the grid (PRIMA pattern).
            Conditional render, not a `collapsed` class — there's no .collapsed
            CSS rule on .search-panel; rendering the node unconditionally would
            keep it visible permanently. */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Schedule"
                value={scheduleInput}
                onChange={(v) => setScheduleInput((v as string) || '')}
                options={scheduleOptions}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={filterLabelStyle}>Group contains</label>
              <input
                type="text"
                className="form-input"
                value={groupInput}
                onChange={e => setGroupInput(e.target.value)}
                placeholder="e.g. Sundry Debtors"
                style={filterInputStyle}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Amount sign"
                value={amountSignInput}
                onChange={(v) => setAmountSignInput(((v as string) || 'all') as 'all' | 'dr' | 'cr')}
                options={[
                  { value: 'all', label: 'All (Dr + Cr)' },
                  { value: 'dr',  label: 'Debits only' },
                  { value: 'cr',  label: 'Credits only' },
                ]}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={filterLabelStyle}>Amount range (absolute)</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="number"
                  className="form-input"
                  value={minAmountInput}
                  onChange={e => setMinAmountInput(e.target.value)}
                  placeholder="Min"
                  min={0}
                  style={{ ...filterInputStyle, flex: 1 }}
                />
                <input
                  type="number"
                  className="form-input"
                  value={maxAmountInput}
                  onChange={e => setMaxAmountInput(e.target.value)}
                  placeholder="Max"
                  min={0}
                  style={{ ...filterInputStyle, flex: 1 }}
                />
              </div>
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Classification source"
                value={classifySourceInput}
                onChange={(v) => setClassifySourceInput(((v as string) || 'all') as 'all' | 'auto' | 'manual')}
                options={[
                  { value: 'all',    label: 'All sources' },
                  { value: 'auto',   label: 'Auto-classified only' },
                  { value: 'manual', label: 'Manually classified only' },
                ]}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* Edit Classification modal (per-row action) */}
      {trialBalanceId && (
        <FsaEditClassificationModal
          isOpen={editEntry != null}
          trialBalanceId={trialBalanceId}
          entry={editEntry}
          templateId={templateId ?? null}
          engagementId={engagementId ?? null}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); refetch(); refetchCustomClasses(); refetchCoverage(); }}
          tabId={tabId}
          moduleName={moduleName}
          onRestoreModal={handleRestoreEditModal}
          minimizeRef={editModalMinimizeRef}
        />
      )}

      {/* Auto-classify modal (toolbar) */}
      {trialBalanceId && (
        <AutoClassifyPreviewModal
          isOpen={autoClassifyOpen}
          trialBalanceId={trialBalanceId}
          onClose={() => setAutoClassifyOpen(false)}
          onApplied={() => { setAutoClassifyOpen(false); refetch(); refetchCoverage(); }}
        />
      )}

      {/* FSA-2.4 — View Details popup. Read-only breakdown that surfaces
          the Consolidated + Journal Net values dropped from the grid. */}
      <FsaTrialBalanceEntryViewModal
        isOpen={viewEntry != null}
        entry={viewEntry}
        audited={viewEntry ? lookupAudited(viewEntry.ledgerName) ?? null : null}
        customClassName={
          viewEntry?.customClassLocalId
            ? customClassNameByLocalId.get(viewEntry.customClassLocalId) ?? null
            : null
        }
        engagementId={engagementId}
        onClose={() => setViewEntry(null)}
      />

      {/* Clear Classification confirm */}
      <ConfirmDialog
        open={clearEntry != null}
        title="Clear Classification"
        message={clearEntry ? `Reset '${clearEntry.ledgerName}' back to unmapped? Any leaf or schedule-only mapping on this ledger will be removed.` : ''}
        confirmLabel="Clear"
        cancelLabel="Cancel"
        variant="danger"
        loading={isClearing}
        loadingMessage="Clearing…"
        onConfirm={handleClearClassification}
        onCancel={() => setClearEntry(null)}
      />

      {/* Mark / Un-mark Control Account confirm */}
      <ConfirmDialog
        open={controlEntry != null}
        title={controlEntry?.isControlAccount ? 'Un-mark Control Account' : 'Mark as Control Account'}
        message={
          controlEntry
            ? controlEntry.isControlAccount
              ? `Remove the control-account flag on '${controlEntry.ledgerName}'? It will rejoin the classification coverage count.`
              : `Mark '${controlEntry.ledgerName}' as a control account? It will be excluded from classification coverage and FS rollups. Any existing classification on this row will be cleared.`
            : ''
        }
        confirmLabel={controlEntry?.isControlAccount ? 'Un-mark' : 'Mark'}
        cancelLabel="Cancel"
        variant={controlEntry?.isControlAccount ? 'default' : 'danger'}
        loading={isFlippingControl}
        loadingMessage="Updating…"
        onConfirm={handleToggleControl}
        onCancel={() => setControlEntry(null)}
      />
    </div>
  );
};

// ============================================================================
// Cell components
// ============================================================================

// FSA-2.4 — compact single-line totals strip between the toolbar and the
// grid. Lives outside the grid (the grid component doesn't support a
// summary row natively, and adding one as a regular data row would mess
// with pagination + sticky headers). Each cell renders inline as
// `LABEL ₹value` so the strip stays one row tall, freeing ~30-40px of
// vertical space vs. the previous 2-line stacked cards.
const totalsStripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  padding: '0.25rem 0.75rem',
  borderTop: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontSize: '0.72rem',
  flexWrap: 'nowrap',
  overflow: 'hidden',
};
const totalsLabelStyle: React.CSSProperties = {
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  flexShrink: 0,
};
const TotalsCell: React.FC<{ label: string; value: number; emphasized?: boolean }> = ({ label, value, emphasized }) => {
  const isDr = value > 0.005;
  const isCr = value < -0.005;
  const nearZero = !isDr && !isCr;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: emphasized ? '1px 8px' : '0',
      borderRadius: 4,
      background: emphasized ? 'var(--bg-primary)' : 'transparent',
      border: emphasized ? '1px solid var(--border-color)' : 'none',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem', fontWeight: 700 }}>
        {label}
      </span>
      <span style={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        color: nearZero ? '#166534' : isDr ? '#1e40af' : '#991b1b',
      }} title={nearZero ? 'Balanced (Dr = Cr)' : isDr ? 'Net debit' : 'Net credit'}>
        {value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </span>
  );
};

const AmountCell: React.FC<{ value: number }> = ({ value }) => {
  const isDr = value > 0;
  const isCr = value < 0;
  return (
    <span style={{
      fontVariantNumeric: 'tabular-nums',
      fontWeight: 600,
      color: isDr ? '#1e40af' : isCr ? '#991b1b' : 'var(--text-secondary)',
      display: 'inline-block',
      width: '100%',
      textAlign: 'right',
    }}>
      {value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

/**
 * Chip rendering the entry's classification state.
 *   - Leaf-classified         → green chip with class name
 *   - Schedule-only (deferred) → amber chip
 *   - Control account         → purple chip (rendered here so the
 *                                Classification column stays informative
 *                                even when the Control Acct column is hidden)
 *   - Otherwise (unmapped)    → gray dash
 */
const ClassificationChip: React.FC<{
  entry: TrialBalanceEntry;
  /** FSA-2.2 step 4b — custom-class id → engagement-time label lookup. */
  customClassName?: string;
}> = ({ entry, customClassName }) => {
  if (entry.isControlAccount) {
    return <span style={chipStyle('#f3e8ff', '#6b21a8')}>⚙ Control Account</span>;
  }
  if (entry.scheduleClassId && entry.scheduleClassName) {
    return <span style={chipStyle('#dcfce7', '#166534')}>{entry.scheduleClassName}</span>;
  }
  if (entry.customClassLocalId) {
    // FSA-2.2 step 4b — engagement-time custom class. Show the auditor-given
    // name when we can resolve it from the engagement workspace; fall back
    // to a generic "Custom class" label when the local_id is unknown
    // (deleted from the Note Modal after the TB was classified to it).
    return (
      <span style={chipStyle('#e0e7ff', '#3730a3')} title="Engagement-time custom class">
        ✦ {customClassName ?? 'Custom class'}
      </span>
    );
  }
  if (entry.scheduleId) {
    return <span style={chipStyle('#fef3c7', '#92400e')} title="Mapped to Note — leaf class pending">📋 Pending class split</span>;
  }
  return <span style={dashStyle}>Unmapped</span>;
};

// ============================================================================
// Helpers
// ============================================================================

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

// ============================================================================
// Styles
// ============================================================================

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};

const filterChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.7rem',
  fontWeight: 600,
  border: '1px solid var(--form-border-color)',
  background: 'var(--bg-primary)',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const filterChipActiveStyle: React.CSSProperties = {
  background: '#2563eb',
  color: '#ffffff',
  borderColor: '#2563eb',
};

const chipCountBadgeStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',
  color: active ? '#ffffff' : 'var(--text-secondary)',
  borderRadius: 999,
  padding: '0 5px',
  fontSize: '0.65rem',
  fontWeight: 700,
  minWidth: 16,
  textAlign: 'center',
});

// FSA-2.4 UI polish — coverage pill rendered next to the chip strip.
// Surfaces % classified at a glance so the auditor can see overall
// progress without filtering. Color follows the coverage band:
//   100% → green; ≥80% → blue; <80% → amber.
const coveragePillStyle = (pct: number): React.CSSProperties => {
  const colors = pct >= 99.5
    ? { bg: '#dcfce7', fg: '#166534' }
    : pct >= 80
      ? { bg: '#dbeafe', fg: '#1e40af' }
      : { bg: '#fef3c7', fg: '#92400e' };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: '0.7rem',
    fontWeight: 700,
    background: colors.bg,
    color: colors.fg,
    whiteSpace: 'nowrap',
  };
};

const searchWrapStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const searchIconStyle: React.CSSProperties = {
  position: 'absolute',
  left: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-secondary)',
  pointerEvents: 'none',
};

const searchInputStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  padding: '0.375rem 0.625rem 0.375rem 1.85rem',
  width: 240,
};

const chipStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: bg,
  color,
  fontSize: '0.7rem',
  fontWeight: 600,
});

const dashStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: '0.85rem',
};

// FSA-2.2 Adjustments — small "JE" badge that flags audited rows backed by
// a journal-introduced ledger (no source TB row, created by a posted JE).
const jeIntroducedBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: -6,
  right: -2,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: '0.6rem',
  fontWeight: 700,
  padding: '1px 4px',
  borderRadius: 3,
  lineHeight: 1,
};

// ADR-0026 — "ADJ" marker on an Opening cell touched by an opening
// adjustment journal. Same shape as the JE badge, opening-journal colour.
const openingAdjBadgeStyle: React.CSSProperties = {
  ...jeIntroducedBadgeStyle,
  background: '#ede9fe',
  color: '#5b21b6',
};

/** Signed amount for tooltips: "1,250.00 Dr" / "800.00 Cr". */
const fmtSigned = (n: number): string => {
  const abs = Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < -0.005 ? `${abs} Cr` : n > 0.005 ? `${abs} Dr` : '0.00';
};

const emptyStateStyle: React.CSSProperties = {
  padding: '3rem 1rem',
  textAlign: 'center',
  color: 'var(--text-primary)',
};

const filterBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: -4,
  right: -4,
  background: '#dc2626',
  color: '#ffffff',
  borderRadius: 999,
  fontSize: '0.625rem',
  fontWeight: 700,
  minWidth: 16,
  height: 16,
  padding: '0 4px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};

const filterLabelStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  marginBottom: '0.25rem',
};

const filterInputStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  padding: '0.375rem 0.625rem',
};

export default FsaTrialBalanceTab;
